/**
 * InviteLinkCard - Link box + copy + expiry + regenerate
 * Shared content between InviteLinkPage (desktop) and InviteLinkSheet (mobile)
 * Design: tripways.pen node x5Vft (desktop) / VxMro (mobile bottom sheet)
 */

import { useEffect, useState } from 'react';
import { Link as LinkIcon, Copy, Clock3, RefreshCw, TriangleAlert } from 'lucide-react';
import ConfirmDialog from '../common/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import { memberService, MemberApiError } from '../../services/memberService';
import { formatDaysUntil } from '../../utils/dates';
import type { InviteLink } from '../../types/member';

interface InviteLinkCardProps {
  tripId: string;
}

export default function InviteLinkCard({ tripId }: InviteLinkCardProps) {
  const { showToast } = useToast();
  const [link, setLink] = useState<InviteLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;

    memberService
      .getInviteLink(tripId)
      .catch((err) => {
        if (err instanceof MemberApiError && err.code === 60002) {
          return memberService.generateInviteLink(tripId);
        }
        throw err;
      })
      .then((result) => {
        if (!cancelled) setLink(result);
      })
      .catch(() => {
        if (!cancelled) showToast('error', '無法載入邀請連結，請稍後再試');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  const shareUrl = link ? `${window.location.origin}/join/${link.token}` : '';
  const displayUrl = link ? `${window.location.host}/join/${link.token}` : '';

  const handleCopy = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    showToast('success', '已複製邀請連結');
  };

  const handleRegenerate = async () => {
    try {
      const newLink = await memberService.generateInviteLink(tripId);
      setLink(newLink);
      showToast('success', '已重新產生邀請連結');
    } catch {
      showToast('error', '重新產生連結失敗，請稍後再試');
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-slate-400 text-sm">載入中...</div>;
  }

  if (!link) {
    return <div className="py-8 text-center text-slate-400 text-sm">無法載入邀請連結</div>;
  }

  const daysLeft = formatDaysUntil(new Date(link.expiresAt));

  return (
    <div className="flex flex-col gap-5 w-full">
      <div className="flex items-start gap-2.5">
        <LinkIcon size={18} className="text-primary mt-0.5 shrink-0" />
        <p className="text-sm text-[#888888] leading-relaxed">分享此連結，讓其他人加入你的旅程</p>
      </div>

      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 h-[52px] pl-4 pr-1">
        <span className="text-sm font-medium text-slate-900 truncate">{displayUrl}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 rounded-[10px] bg-primary text-white text-[13px] font-semibold px-4 h-10 shrink-0"
        >
          <Copy size={15} />
          複製
        </button>
      </div>

      <div className="flex items-center gap-2 text-amber-500 text-[13px] font-medium">
        <Clock3 size={14} />
        <span>此連結將在 {daysLeft} 天後失效</span>
      </div>

      <div className="h-px bg-slate-100" />

      <div className="flex flex-col gap-3">
        <button
          onClick={() => setShowRegenConfirm(true)}
          className="flex items-center justify-center gap-2 h-12 rounded-xl border border-slate-200 text-slate-900 text-[15px] font-semibold min-h-[44px]"
        >
          <RefreshCw size={16} />
          重新產生連結
        </button>
        <div className="flex gap-2 rounded-[10px] bg-amber-100 px-3 py-2.5">
          <TriangleAlert size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            重新產生後，舊連結將立即失效，已收到連結的人將無法使用。
          </p>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showRegenConfirm}
        onClose={() => setShowRegenConfirm(false)}
        onConfirm={handleRegenerate}
        title="重新產生邀請連結？"
        message="舊連結會立即失效，已經收到舊連結的人將無法用它加入旅程。"
        confirmText="重新產生"
      />
    </div>
  );
}
